/**
 * purchase-invoice-columns.tsx — wspólna konfiguracja KOLUMN listy faktur zakupu.
 *
 * Jedno źródło prawdy dla dwóch ekranów, które pokazują faktury zakupu jako tabelę z kolumnami
 * „jak na liście": strony `PurchaseInvoices` oraz sekcji kokpitu decyzji (`pages/processes/
 * decision-sections`, „Grupuj według: Kartoteka"). Zmiana tutaj jest widoczna na obu — kolumny
 * nie mogą się rozjechać.
 *
 * Zawartość: rejestr nadpisań renderu kolumn rdzennych (`PURCHASE_INVOICE_OVERRIDES`), slugi
 * obsługiwane osobno (`PI_EXCLUDE_SLUGS`), remap sortu (`PI_SORT_SLUG_OVERRIDES`), slugi dołączane
 * do `?columns=` (`PI_ALWAYS_SLUGS`, `PI_COLUMN_COMPANIONS`), mapa id kolumny → slug backendu
 * (`PI_COLUMN_ID_TO_SLUG`), prefiks kluczy i18n kolumn procesu (`PI_PROCESS_LABEL_PREFIX` — kolumny
 * buduje wspólna fabryka z `process-columns.tsx`) i typ wiersza listy (`InvoiceRow`). Kolumny
 * rdzenne/dodatkowe buduje katalog pól (`buildCoreColumns`/`buildExtColumns` z `ext-list-columns`);
 * tu jest tylko to, co jest specyficzne dla faktur zakupu.
 */

import { SourceBadge } from '@/components/purchase-invoices/SourceBadge';
import {
  INVOICE_STATUS_CODES,
  invoiceExportTextOverrides,
  invoiceKindFilterOptions,
  invoiceKindLabel,
  invoiceSourceFilterOptions,
  invoiceStatusLabel,
} from '@/lib/documents/invoice-select-labels';
import {
  jpkDocumentMarkerFilterOptions,
  jpkDocumentMarkerLabel,
  purchaseEvidenceMarkerFilterOptions,
  purchaseEvidenceMarkerLabel,
} from '@/lib/documents/ksef-jpk-markers';
import { PROCESS_COLUMN_ID_TO_SLUG } from '@/lib/documents/process-columns';
import {
  buildStaticSelectOverride,
  type ColumnCompanionRule,
  type CoreColumnOverride,
  type CoreColumnTranslateFn,
  isFieldFilterable,
} from '@/lib/ext-list-columns';

/**
 * Slugi rdzenne obsługiwane OSOBNO (dedykowane buildery z filtrami record-select), więc pomijane
 * przez `buildCoreColumns` — inaczej powstałyby dwie kolumny na to samo pole.
 */
export const PI_EXCLUDE_SLUGS: ReadonlySet<string> = new Set(['company_id', 'owner_user_id']);

/**
 * Nadpisania slug→slug-sortu dla `?sort=` (reszta: id kolumny = slug, fallback w
 * `useServerListControls`). „Firma" (`buildCompanyColumn`, id `company`) sortu nie ma (nieobecna).
 * „Dostawca" (id `vendor_id`, referencja) sortuje się po buforowanej NAZWIE (`vendor_name_cached`),
 * nie po uuid — dlatego remap; `isCoreColumnSortable('vendor_id')` też przez to sprawdza nazwę.
 */
export const PI_SORT_SLUG_OVERRIDES: Readonly<Record<string, string>> = {
  vendor_id: 'vendor_name_cached',
};

/**
 * Slugi dołączane do `?columns=` ZAWSZE, niezależnie od widocznych kolumn: `gcrecord` (akcja wiersza
 * restore/delete) i `company_id` (firma wiersza dla akcji zbiorczych — dekretacja: bramka „jedna
 * firma” + reguły firmy; kolumny „Firma” zwykle nie ma na liście, a projekcja zwraca tylko żądane
 * pola, więc bez tego modal „Dekretuj” dostawał pustą listę firm).
 */
export const PI_ALWAYS_SLUGS: readonly string[] = ['gcrecord', 'company_id'];

/**
 * Reguły towarzysza dla `?columns=`: „Dostawca" (composite) czyta nazwę i NIP; kwoty
 * (brutto/netto/VAT) czytają walutę NIEZALEŻNIE od tego, która z trzech jest widoczna — inaczej
 * kolumna „Kwota netto" widoczna bez „Kwota brutto" pokazywałaby cichy fallback 'PLN' na fakturze
 * walutowej.
 */
export const PI_COLUMN_COMPANIONS: readonly ColumnCompanionRule[] = [
  { whenSlug: 'vendor_id', addSlug: 'vendor_name_cached' },
  { whenSlug: 'vendor_id', addSlug: 'vendor_tax_id_cached' },
  { whenSlug: 'amount_gross', addSlug: 'currency_code' },
  { whenSlug: 'amount_net', addSlug: 'currency_code' },
  { whenSlug: 'amount_vat', addSlug: 'currency_code' },
];

/**
 * Mapa id kolumny → slug backendu dla `?columns=` — część STAŁA: kolumny spoza `buildCoreColumns`
 * (firma ma id `company`, kategoria `category`, opiekun) i kolumny procesu (wspólna mapa wszystkich
 * klas dokumentów, `process-columns.tsx`). Kolumny z katalogu (`buildCoreColumns`) dopisuje wołający
 * pętlą `id = slug`.
 */
export const PI_COLUMN_ID_TO_SLUG: Readonly<Record<string, string>> = {
  company: 'company_id',
  category: 'category',
  owner_user_id: 'owner_user_id',
  ...PROCESS_COLUMN_ID_TO_SLUG,
};

/**
 * Prefiks kluczy i18n kolumn procesu faktur zakupu — podawany wspólnej fabryce `buildProcessColumns`
 * (`process-columns.tsx`) przez stronę faktur i sekcję kokpitu decyzji.
 */
export const PI_PROCESS_LABEL_PREFIX = 'tenant.purchaseInvoices';

/** Prefiks kluczy i18n etykiet rodzaju faktury i źródła na liście faktur zakupu. */
const PI_LABEL_PREFIX = 'tenant.purchaseInvoices';

function formatAmount(amount: string | null | undefined, currency: string): string {
  if (amount == null) return '—';
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(n);
}

/**
 * Rejestr nadpisań kolumn rdzennych faktur zakupu (per-kartoteka) — render bogaty tam, gdzie
 * generyk (typ→komórka) nie wystarcza. Standalone (poza komponentem): sortowalność bierze z
 * `ctx.resolveSortable(ctx.field)` (ta sama serwerowa reguła co generyk, z remapem sortu przez
 * `PI_SORT_SLUG_OVERRIDES`), więc nadpisania nie muszą domykać `controls`/`sortableSlugs`.
 * Klucze = slugi pól z katalogu (`system_module_fields.slug`).
 */
export const PURCHASE_INVOICE_OVERRIDES: Readonly<Record<string, CoreColumnOverride<InvoiceRow>>> =
  {
    // Status — nazwy jak na karcie faktury (+ `booked`, `new`, `verified` z KSeF), NIE nazwy ze słownika
    // pola (patrz `INVOICE_STATUS_CODES`); nadpisanie omija ścieżkę słownikową generyka.
    status: buildStaticSelectOverride<InvoiceRow>({
      codes: INVOICE_STATUS_CODES,
      labelOf: invoiceStatusLabel,
    }),
    system_number: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 160,
      sortable: resolveSortable?.(field) ?? false,
      headerFilter: { category: 'text', fieldSlug: 'system_number' },
      renderCell: (value) => (
        <span className="font-mono text-xs text-text-secondary">{value ? String(value) : '—'}</span>
      ),
    }),
    invoice_number: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 160,
      hideable: false,
      sortable: resolveSortable?.(field) ?? false,
      headerFilter: { category: 'text', fieldSlug: 'invoice_number' },
      renderCell: (value) => <span className="font-medium text-text-primary">{String(value)}</span>,
    }),
    // „Dostawca" = pole `vendor_id` (referencja do kontrahenta, bez tłumacza etykiety) — renderujemy
    // buforowaną NAZWĘ + NIP (composite, dane z `vendor_name_cached`/`vendor_tax_id_cached`, dołączane
    // do projekcji regułą towarzysza `PI_COLUMN_COMPANIONS`). Filtr = alias similarity `vendor_name` (nie `vendor_id`).
    vendor_id: ({ field, resolveSortable }) => ({
      id: field.slug,
      accessor: 'vendorNameCached',
      header: field.name,
      defaultVisible: true,
      defaultWidth: 240,
      sortable: resolveSortable?.(field) ?? false,
      headerFilter: { category: 'text', fieldSlug: 'vendor_name' },
      renderCell: (_value, row) => (
        <div className="text-text-primary">
          {row.vendorNameCached ?? '—'}
          {row.vendorTaxIdCached ? (
            <span className="ml-2 text-xs text-text-muted">NIP {row.vendorTaxIdCached}</span>
          ) : null}
        </div>
      ),
    }),
    // Typ dokumentu (FA(3): VAT/KOR/…) — raw kod z backendu mapowany na etykietę i18n (jak Contractors
    // objectType / Users userType), nie przez słownik katalogu. Lista wyboru w wierszu filtrów
    // (wartość = kod z bazy) i komórka biorą etykietę z TEJ SAMEJ funkcji (`invoiceKindLabel`); bez
    // `options` wiersz filtrów spadał na martwe pole „Szukaj…" zamiast listy wyboru.
    // `transformLabel` zostaje dla popupu nagłówka i dla torów Kanbana z wartościami spoza `options`
    // (`KanbanBoard` etykietuje nim taki tor). Filtr tylko wtedy, gdy serwer deklaruje pole
    // (`isFieldFilterable`); `filterable: false` — ta kolumna ma listę wyboru, nigdy pole „Szukaj…".
    document_type: ({ field, accessor, t, resolveSortable, resolveFilterable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 150,
      sortable: resolveSortable?.(field) ?? false,
      filterable: false,
      ...(isFieldFilterable(field, resolveFilterable)
        ? {
            headerFilter: {
              category: 'select' as const,
              fieldSlug: 'document_type',
              options: invoiceKindFilterOptions(t, PI_LABEL_PREFIX),
              kanbanFallback: false as const,
              transformLabel: (value: string | null) =>
                value ? invoiceKindLabel(value, t, PI_LABEL_PREFIX) : t('common.lists.emptyValue'),
            },
          }
        : {}),
      renderCell: (value) => (
        <span className="text-text-secondary">
          {value ? invoiceKindLabel(String(value), t, PI_LABEL_PREFIX) : '—'}
        </span>
      ),
    }),
    // Ewidencja KSeF/JPK (0159) — nadpisania KONIECZNE, bo generyk dla pola typu „lista wyboru"
    // bez podpiętego słownika pokazałby surowy kod (`BFK`, `WEW`) i takie same pozycje w filtrze
    // nagłówka. Zbiór jest zamknięty w kodzie (`@saas/shared`), więc etykiety dokłada renderer, a
    // TA SAMA funkcja etykiety zasila komórkę i opcje listy wyboru w wierszu filtrów.
    purchase_evidence_marker: ({ field, accessor, t, resolveSortable, resolveFilterable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 220,
      sortable: resolveSortable?.(field) ?? false,
      filterable: false,
      ...(isFieldFilterable(field, resolveFilterable)
        ? {
            headerFilter: {
              category: 'select' as const,
              fieldSlug: 'purchase_evidence_marker',
              options: purchaseEvidenceMarkerFilterOptions(t),
              kanbanFallback: false as const,
              transformLabel: (value: string | null) =>
                value
                  ? purchaseEvidenceMarkerLabel(String(value), t)
                  : t('common.lists.emptyValue'),
            },
          }
        : {}),
      renderCell: (value) => (
        <span className="text-text-secondary">
          {value ? purchaseEvidenceMarkerLabel(String(value), t) : '—'}
        </span>
      ),
    }),
    jpk_document_marker: ({ field, accessor, t, resolveSortable, resolveFilterable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 170,
      sortable: resolveSortable?.(field) ?? false,
      filterable: false,
      ...(isFieldFilterable(field, resolveFilterable)
        ? {
            headerFilter: {
              category: 'select' as const,
              fieldSlug: 'jpk_document_marker',
              options: jpkDocumentMarkerFilterOptions(t),
              kanbanFallback: false as const,
              transformLabel: (value: string | null) =>
                value ? jpkDocumentMarkerLabel(String(value), t) : t('common.lists.emptyValue'),
            },
          }
        : {}),
      renderCell: (value) => (
        <span className="text-text-secondary">
          {value ? jpkDocumentMarkerLabel(String(value), t) : '—'}
        </span>
      ),
    }),
    // Kwota brutto + waluta w jednej komórce (companion `currency_code` dołączany do projekcji
    // regułą towarzysza `PI_COLUMN_COMPANIONS`). `headerFilter` category `number` — BEZ tego
    // nadpisanie gubiło filtr liczbowy: kolumna spadała na legacy „Szukaj…" (input bez fieldSlug —
    // wpisana wartość nigdzie nie leci) zamiast operatorów >,<,>=,<=,= (silnik-list-filtr-nagłówka).
    amount_gross: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 140,
      sortable: resolveSortable?.(field) ?? false,
      dataType: 'number',
      headerFilter: { category: 'number', fieldSlug: 'amount_gross' },
      renderCell: (_value, row) => (
        <span className="tabular-nums text-text-primary">
          {formatAmount(row.amountGross, row.currencyCode ?? 'PLN')}
        </span>
      ),
    }),
    // Kwota netto + waluta — mirror `amount_gross` (poszerzenie listy, silnik-list-filtr-nagłówka).
    // Companion `currency_code` dołączany do projekcji (`PI_COLUMN_COMPANIONS`) jak przy `amount_gross`.
    amount_net: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 140,
      sortable: resolveSortable?.(field) ?? false,
      dataType: 'number',
      headerFilter: { category: 'number', fieldSlug: 'amount_net' },
      renderCell: (_value, row) => (
        <span className="tabular-nums text-text-primary">
          {formatAmount(row.amountNet, row.currencyCode ?? 'PLN')}
        </span>
      ),
    }),
    // Kwota VAT + waluta — mirror `amount_gross`/`amount_net`.
    amount_vat: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 140,
      sortable: resolveSortable?.(field) ?? false,
      dataType: 'number',
      headerFilter: { category: 'number', fieldSlug: 'amount_vat' },
      renderCell: (_value, row) => (
        <span className="tabular-nums text-text-primary">
          {formatAmount(row.amountVat, row.currencyCode ?? 'PLN')}
        </span>
      ),
    }),
    // Źródło (manual/symfonia/ksef) — plakietka `SourceBadge` (inny słownik niż wolnotekstowe
    // `documents.source` klas STANDARD). Opcje listy wyboru w wierszu filtrów = te same trzy kody z
    // tymi samymi kluczami i18n, które czyta plakietka (`tenant.purchaseInvoices.source.*`).
    source: ({ field, t, resolveSortable, resolveFilterable }) => ({
      id: field.slug,
      accessor: 'source',
      header: field.name,
      defaultVisible: true,
      defaultWidth: 120,
      sortable: resolveSortable?.(field) ?? false,
      filterable: false,
      ...(isFieldFilterable(field, resolveFilterable)
        ? {
            headerFilter: {
              category: 'select' as const,
              fieldSlug: 'source',
              options: invoiceSourceFilterOptions(t, PI_LABEL_PREFIX),
              kanbanFallback: false as const,
            },
          }
        : {}),
      renderCell: (_value, row) => <SourceBadge source={row.source ?? null} />,
    }),
  };

/**
 * `id` (klucz React), `gcrecord` (akcja wiersza restore/delete), `companyId` (akcje zbiorcze —
 * slugi `gcrecord`/`company_id` są zawsze w `alwaysSlugs`) oraz `invoiceNumber` (kolumna
 * `hideable: false`) są jedynymi polami GWARANTOWANYMI w każdej odpowiedzi —
 * reszta zależy od aktualnie widocznych kolumn (projekcja `?columns=` zwraca tylko zażądane pola).
 * Typ wiersza listy faktur zakupu; wiersz sekcji kokpitu decyzji = ten wiersz + kroki decyzji.
 */
export interface InvoiceRow {
  id: string;
  companyId?: string;
  vendorId?: string;
  systemNumber?: string | null;
  invoiceNumber: string;
  ksefNumber?: string | null;
  purchaseEvidenceMarker?: string | null;
  jpkDocumentMarker?: string | null;
  documentType?: string;
  status?: string;
  urgency?: string;
  issueDate?: string;
  receivedDate?: string;
  paymentDueDate?: string;
  vendorTaxIdCached?: string | null;
  vendorNameCached?: string | null;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze) — kolumna „Kategoria". */
  categoryName?: string | null;
  currencyCode?: string;
  amountNet?: string;
  amountVat?: string;
  amountGross?: string;
  source?: string;
  externalId?: string | null;
  externalSource?: string | null;
  ownerUserId?: string | null;
  gcrecord: string | null;
  /**
   * Kolumny przekrojowe silnika list (`apps/api/lib/list-engine/providers/process-columns.ts`) —
   * DOKŁADANE na każdym wierszu gdy tenant ma aktywny moduł `processes`, POMIJANE całkowicie (klucz
   * nieobecny) gdy moduł nieaktywny. `currentStage` = bieżące etapy sprawy w toku (0..N, gałęzie
   * równoległe) albo nazwa etapu końcowego sprawy zakończonej (jeden element).
   */
  activeProcessName?: string | null;
  currentStage?: string[];
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie projekcji
   * `?columns=` i zażądano ≥1 aktywnego pola ext.
   */
  ext?: Record<string, unknown>;
}

/** Tekst komórek z ozdobnikami do pliku CSV (eksport widoku listy) — patrz `invoiceExportTextOverrides`. */
export function purchaseInvoiceExportTextOverrides(t: CoreColumnTranslateFn) {
  return invoiceExportTextOverrides<InvoiceRow>(t, PI_LABEL_PREFIX);
}
